import * as Sentry from '@sentry/react'
import CookieManager from './CookiesManager'

const SENTRY_DSN =
  'https://aeb1c56fdcae173b9ec278110a1b5fc3@o4508517432688640.ingest.de.sentry.io/4508517435572304'

class SentryManager {
  private static instance: SentryManager
  private initialized: boolean = false

  private constructor() {}

  static getInstance(): SentryManager {
    if (!SentryManager.instance) {
      SentryManager.instance = new SentryManager()
    }
    return SentryManager.instance
  }

  init(): void {
    if (this.initialized) return

    const consent = CookieManager.getConsent()
    if (!consent.analytics) {
      return
    }

    try {
      Sentry.init({
        dsn: SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0,
        environment: 'production',
        tracePropagationTargets: [
          'sexagogo.be',
          /^https:\/\/sexagogo.be\/api/,
          /^https:\/\/sexagogo.be/,
        ],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 0.1,
        beforeSend(event) {
          if (event.request?.url) {
            event.request.url = event.request.url.replace(
              /(http|https):\/\/[^/]+/g,
              'http://[REDACTED]',
            )
          }

          if (event.user?.ip_address) {
            delete event.user.ip_address
          }

          if (event.breadcrumbs) {
            event.breadcrumbs.forEach(breadcrumb => {
              if (breadcrumb.data?.sensitiveInfo) {
                delete breadcrumb.data.sensitiveInfo
              }
            })
          }

          return event
        },
      })
      this.initialized = true
    } catch (error) {
      console.error('Failed to initialize Sentry:', error)
    }
  }

  updateConsent(hasAnalyticsConsent: boolean): void {
    if (!hasAnalyticsConsent && this.initialized) {
      this.shutdown()
    } else if (hasAnalyticsConsent && !this.initialized) {
      this.init()
    }
  }

  shutdown(): void {
    if (!this.initialized) return

    try {
      Sentry.close()
      this.initialized = false
      document.cookie =
        'sg_sentry=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure; samesite=strict'
    } catch (error) {
      console.error('Failed to close Sentry:', error)
    }
  }

  captureException(error: Error) {
    if (import.meta.env.VITE_SENTRY_DSN) {
      try {
        Sentry.captureException(error)
      } catch (err) {
        console.error('Error capturing exception to Sentry:', err)
      }
    } else {
      console.error('Sentry error:', error)
    }
  }
}

export default SentryManager
